/* eslint-disable max-lines, eqeqeq */
import { extendObservable, action, computed } from 'mobx';

import AccessLevelService from 'services/AccessLevelService';
import FirebaseRoomService from 'services/FirebaseRoomService';
import FirebaseService, { FirebaseServiceClass } from 'services/FirebaseService';
import {
  FB_SET_AUTH_DATA,
  FIREBASE_DEBUG,
  FIREBASE_INITIAL_DATA,
  FIREBASE_UNAVAILABLE,
  GET_ACTIVE_GAME_INFO_TRIGGER,
  GET_USER_DATA_RESPONSE,
  INIT_ROOM,
  MODERATOR_PRIVILEGE_CHANGED,
  PRIVATE_SHOW_REQUEST_SENT,
  REINIT_ROOM,
  REQUEST_PLAY_TOKEN,
  RESET_PRIVATE_ROOM,
  RESET_SHOW_STATE,
  ROOM_LOADING_STATUS,
  ROOM_STATE_CHANGED_RESPONSE,
  SET_BROADCASTER_BANNED_USERS_IN_ROOM,
  SET_BROADCASTER_MODERATORS_IN_ROOM,
  SET_BROADCASTER_PRIVATE_SHOW_INFO,
  SET_BROADCASTER_SILENCED_USERS_IN_ROOM,
  SET_JWT_TOKEN,
  SHOW_ENDED,
  UI_ANALYTICS_ROOM_INIT,
  UPDATE_STREAM_UUID,
  UPDATE_WEB_PRESENCE_CHAT_STATUS,
  USER_BROADCAST_STARTED,
  USER_HAS_BEEN_KICKED_OR_BANNED,
  USER_OFFLINE_NOTIFICATION_RECEIVED,
} from 'services/MessagingService/EventNames';
import messagingService from 'services/MessagingService/MessagingService';
import TrackingService from 'services/TrackingService';
import BroadcasterPrivateShowStore from 'stores/BroadcasterPrivateShowStore';
import ChatStore from 'stores/ChatStore';
import chatBotsStore from 'services/chatBotsService/chatBotsStore';
import firebaseStoreInitialState from 'stores/FirebaseStoreInitialState';
import ProfileStore from 'stores/ProfileStore';
import UserStore from 'stores/UserStore';
import { isAnyTypeOfGold, ACCESS_LEVEL_STR } from 'pages/User/AccessLevel';
import APIStates from 'enum/APIStates';
import controlledFeatures from 'enum/controlledFeatures';
import { NOTIFICATION_TYPE } from 'enum/messages';
import routeDefinitions from 'enum/routeDefinitions';
import allUserGroups from 'enum/userGroups';
import { SECOND_AS_MILLISECONDS } from 'enum/timeFormat';
import { NEW_SHOWS_STATE, SHOW_TYPE } from 'enum/videoPlayer';
import { addPercentage, parseLightRosterUser } from 'utils/helpers';
import { FIREBASE_AUTH_STATUS } from 'firebase/firebaseStructure';


const STATUS_TIMEOUT_IN_SECONDS = 20;
const STREAM_DETAILS_DELAY = 16;
const STREAM_DETAILS_DELAY_MS = STREAM_DETAILS_DELAY * SECOND_AS_MILLISECONDS;

const EXTENDED_GS_STATES = [NEW_SHOWS_STATE.INSIDE_GS, NEW_SHOWS_STATE.BONUS_GS];

const MAX_ROOM_REQUEST_RETRIES = 5;
let roomRequestRetries = 0;

const getPerformerLocationLowerName = () => {
  const [, performerSourceName] = window.location.pathname.split('/');

  // BETA DIRECTORY RELATED - STARTS
  const searchParams = new URLSearchParams(window.location.search);
  const performerName = searchParams.get('performer');
  const isBeta = searchParams.get('beta');

  if (isBeta && performerName) {
    return performerName.toLowerCase();
  }
  // BETA DIRECTORY RELATED - ENDS

  return performerSourceName ? performerSourceName.toLowerCase() : '';
};

const isPerformerMatchRoomName = (roomSourceName) => {
  const roomName = String(roomSourceName || '')
    .toLowerCase()
    .trim();

  if (!roomName) {
    return false;
  }

  const performerLowerName = getPerformerLocationLowerName();

  return performerLowerName === roomName || performerLowerName === 'broadcast';
};

const isBroadcastWindow = location.pathname === routeDefinitions.broadcaster;

class FirebaseStore {
  roomInitialized = undefined;

  constructor() {
    extendObservable(this, firebaseStoreInitialState);
    this.messagingService = messagingService();
    this.messagingService.on(PRIVATE_SHOW_REQUEST_SENT, this.togglePrivateShowRequest);
    this.messagingService.on(USER_HAS_BEEN_KICKED_OR_BANNED, this._handleKickOrBan);
    this.messagingService.on(RESET_PRIVATE_ROOM, this.resetPrivateRoom);
    this.messagingService.on(SET_JWT_TOKEN, this._setJwt);
    this.messagingService.on(INIT_ROOM, this._handleInitRoom);
    this.messagingService.on(GET_USER_DATA_RESPONSE, this._checkFirebaseStatus);
    this.messagingService.on(RESET_SHOW_STATE, this.resetNewShowState);
    this.firebaseDataReceived = false;
    this.notificationFirebaseService = new FirebaseServiceClass();
  }

  destructor() {
    this.messagingService.off(PRIVATE_SHOW_REQUEST_SENT, this.togglePrivateShowRequest);
    this.messagingService.off(USER_HAS_BEEN_KICKED_OR_BANNED, this._handleKickOrBan);
    this.messagingService.off(RESET_PRIVATE_ROOM, this.resetPrivateRoom);
    this.messagingService.off(SET_JWT_TOKEN, this._setJwt);
    this.messagingService.off(INIT_ROOM, this._handleInitRoom);
    this.messagingService.off(GET_USER_DATA_RESPONSE, this._checkFirebaseStatus);
    this.messagingService.off(RESET_SHOW_STATE, this.resetNewShowState);
    this.removeFirebaseListeners();
  }

  _checkFirebaseStatus = (user) => {
    if (!user || !user.controlledFeatures) {
      return;
    }
    if (user.controlledFeatures.indexOf(controlledFeatures.FIREBASE_WARNING) < 0) {
      return;
    }
    this.messagingService.off(GET_USER_DATA_RESPONSE, this._checkFirebaseStatus);
    const requestTimeout = (time) => {
      const controller = new window.AbortController();
      setTimeout(() => controller.abort('TIMEOUT'), time * SECOND_AS_MILLISECONDS);

      return controller;
    };
    const { signal } = requestTimeout(STATUS_TIMEOUT_IN_SECONDS);
    window
      .fetch(`https://cam4.firebaseio.com/?q=${+new Date()}`, {
        redirect: 'manual',
        signal,
      })
      .catch(() => {
        this.messagingService.emit(FIREBASE_UNAVAILABLE);
      });
  };

  @action _setJwt = (token) => {
    this.apiToken = token;
  };

  @action updateAuthError(authError) {
    this.authError = authError;
  }

  @action updateUserInRoster(username, userObj) {
    this.webUsers = {
      ...this.webUsers,
      [username]: userObj,
    };
  }

  removeFirebaseListeners = (roomName) => {
    const chatlessRoomUserCountRefLight = FirebaseService.watchThread(roomName, 'chatlessRoomUserCountRefLight');
    const roomStateRef = FirebaseService.watchThread(roomName, 'roomStateRef');
    const broadcastingStatusRef = FirebaseService.watchThread(roomName, 'broadcastingStatus');
    const userOfflineNotificationRef = this.notificationFirebaseService.watchThread(
      roomName,
      'userOfflineNotificationRef'
    );
    FirebaseRoomService.removeRosterListener(roomName);
    roomStateRef?.off();
    chatlessRoomUserCountRefLight?.off();
    broadcastingStatusRef?.off();
    userOfflineNotificationRef?.off();
    FirebaseRoomService.removeWebPresence();
    if (FirebaseService.FirebaseMain) {
      FirebaseService.FirebaseMain.offAuth(FirebaseService._onAuthLog);
    }
  };

  @action removeUserFromRoster(username) {
    const deepCopy = Object.assign({}, this.webUsers);
    delete deepCopy[username];
    this.webUsers = deepCopy;
  }

  @action setPrivateRoomLoadingStatus = (privateRoomLoading) => {
    this.privateRoomLoading = privateRoomLoading;
  };

  @action setPrivateShowLoadingButtonState = (privateShowLoadingButtonState) => {
    this.privateShowLoadingButtonState = privateShowLoadingButtonState;
  };

  @action cancelStatePrivateShow = (cancelLoadingStatePrivateShow) => {
    this.cancelLoadingStatePrivateShow = cancelLoadingStatePrivateShow;
  };

  // eslint-disable-next-line complexity
  @action populateRoomStateData(roomState) {
    this.messagingService.emit(FIREBASE_DEBUG, {
      event: 'populateRoomStateData',
      broadcastStatus: ProfileStore.broadcastStatus,
      newRoomState: roomState,
      oldStatus: this.status,
      oldPrivateRoom: this.privateRoom,
      oldShowsState: this.newShowsState,
    });
    if (roomState) {
      this.messagingService.emit(ROOM_LOADING_STATUS, APIStates.LOADED);
      const user = UserStore.user || {};
      const roomName = ProfileStore.profileName;

      this.allowCamToCam = roomState.allow_c2c;
      this.c2cPricePerMinute = roomState.c2c_ppm;
      this.streamingProvider = roomState.prov;
      this.allowPrivateShow = roomState.allow_ps;
      this.allowSpyShow = roomState.allow_spy === '1';
      this.broadcastStartTime = roomState.st;

      if (roomState.sss) {
        this.authData.publicHls = roomState.hls;
        this.authData.useStreamingSaas = true;
        this.authData.privateRoom = roomState.privateRoom;
        this.authData.privateStream = !!roomState.prs;

        if (this.authData.privateStream && !isBroadcastWindow) {
          const tokenPayload = { roomName, username: user.username, accessHash: user.accessHash };
          if (this.newShowsState === NEW_SHOWS_STATE.PAUSED && roomState.newShowsState === NEW_SHOWS_STATE.ACCEPTING) {
            this.online = null;
            this.messagingService.emit(REINIT_ROOM, tokenPayload);
          } else if (this.online !== null && !this.online && roomState.online) {
            this.messagingService.emit(REINIT_ROOM, tokenPayload);
          }
        }
      } else {
        this.authData.useStreamingSaas = false;
      }

      if (this.online !== roomState.online) {
        TrackingService.track({ performerOnline: this.online ? 1 : 0 });
      }

      this.online = roomState.online;

      if (!isBroadcastWindow && ((this.privateRoom && !roomState.privateRoom) ||
      (this.newShowsState === NEW_SHOWS_STATE.PAUSED && roomState.newShowsState !== NEW_SHOWS_STATE.PAUSED))) {
        ProfileStore.setPlayToken(undefined);
      }

      if (
        !this.privateRoom &&
        roomState.privateRoom &&
        this.privateRoom !== null &&
        !isBroadcastWindow
      ) {
        // TODO: please use this.handleReInitRoom
        FirebaseRoomService.removeWebPresence();
        if (FirebaseService.FirebaseMain) {
          FirebaseService.FirebaseMain.offAuth(FirebaseService._onAuthLog);
        }
        this.messagingService.emit(REINIT_ROOM, {
          roomName,
          username: user.username,
          accessHash: user.accessHash,
        });
      }

      this.privateRoom = roomState.privateRoom;
      this.newShowsState = roomState.newShowsState;
      this.status = roomState.status;
      this.streamSource = roomState.streamSource;
      this.fanclubImageId = roomState.fanclubImageId;
      const streamUUIDHasChanged = this.streamUUID && roomState.streamUUID && this.streamUUID !== roomState.streamUUID;
      if (streamUUIDHasChanged && !isBroadcastWindow) {
        this.messagingService.emit(UPDATE_STREAM_UUID, { newStreamUUID: roomState.streamUUID });
      }
      this.streamUUID = roomState.streamUUID;
      this.uuid = roomState.uuid;
      this.streamResolution = roomState.rez;
      this.spyPricePerMinute = roomState.spy_ppm;
      this.privateShowBlocksOfTime = roomState.ps_bt;
      this.privateShowBlocksOfTimeActive = roomState.ps_bt_a;
      this.viewerPricePerMinute = roomState.viewer_ppm;
      this.originUrl = roomState.originUrl;
      this.broadcastId = roomState.bId;
      this.broadcastType = roomState.bType;
      this.mbaOrientation = roomState.mba_o;
      this.mbaVersion = roomState.mba_v;
      this.tippingSessionId = roomState.tsId;
      this.kiirooRoom = roomState.kiirooRoom ? JSON.parse(roomState.kiirooRoom) : false;
      this.newShowsDetails = roomState.newShowsDetails;
      this.disconnectTimestamp = roomState.dTS;
      this.boostRemainingTime = roomState.bret || null;
      this.privateShowMinMinutes = roomState.ps_icm;
      this.pauseTimestamp = roomState.pTS;
      this.kcid = roomState.kcid;
      this.externalViewersCount = roomState.pvc;

      this.messagingService.emit(ROOM_STATE_CHANGED_RESPONSE, roomState);
    }
    TrackingService.track({ performerOnline: this.online ? 1 : 0 });

    if (!this.firebaseDataReceived) {
      this.messagingService.emit(FIREBASE_INITIAL_DATA, roomState);
      this.firebaseDataReceived = true;
    }
  }

  @action handleReInitRoom() {
    const roomData = {
      roomName: ProfileStore.profileName.toLowerCase(),
      username: UserStore.user.username,
      accessHash: UserStore.user.accessHash,
    };

    this.messagingService.emit(FIREBASE_DEBUG, {
      event: 'handleReInitRoom',
      ...roomData,
    });
    FirebaseRoomService.removeWebPresence();
    if (FirebaseService.FirebaseMain) {
      FirebaseService.FirebaseMain.offAuth(FirebaseService._onAuthLog);
    }
    this.messagingService.emit(REINIT_ROOM, roomData);
  }

  // Init firebase room with room state watcher and room roster watcher
  @action initRoom(rawRoomName, username, accessHash, pass, authDone, performerName) {
    let timeoutId = null;
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    const roomName = (rawRoomName || '').toLowerCase();
    const sanitizedUsername = username ? username.toLowerCase() : '';
    const isGuest = (!username || username === '') && (!accessHash || accessHash === '');
    let roomStatus = null;
    let chatHistory = null;

    return FirebaseRoomService.initRoom(roomName, username, accessHash, pass)
      .then((response) => {
        if (isPerformerMatchRoomName(roomName, performerName)) {
          roomStatus = response.data.status;
          chatHistory = response.data.chat_history || null;

          this.messagingService.emit(UI_ANALYTICS_ROOM_INIT, { room: roomName, username });

          this.isGuest = isGuest;

          // useStreamingSaas flows
          // we do not able to fully store the response.data to another property (when private)
          // because too many related changes
          // so just move the hls to another property for now

          // nothing->private current hls is private
          // nothing->public current hls is public
          // public->private current hls is private
          // public->public current hls is public
          // private->public current hls is public
          if (response.data.useStreamingSaas === true) {
            this.messagingService.emit(REQUEST_PLAY_TOKEN, response.data);
            if (response.data.privateStream === true && response.data.privateRoom === true) {
              // the hls from response is private hls
              // the authData.publicHls is public hls
              // so, save actual hls AND store new private hls
              this.authData = {
                ...response.data,
                publicHls: this.authData.publicHls,
                privateHls: response.data.hls,
              };
            } else {
              // basic flow with public
              // just override actual data
              this.authData = {
                ...response.data,
                privateHls: '',
                publicHls: response.data.hls,
              };
            }
          } else {
            // old wowza flow
            this.authData = response.data;
          }

          if (UserStore.hasCF(allUserGroups.STREAMING_SAAS_WOWZA)) {
            this.authData = response.data;
          }

          this.messagingService.emit(FB_SET_AUTH_DATA, this.authData);

          this.updateAuthError(FirebaseRoomService.authDataStatusHandler(response.data.status, isGuest));

          this.apiToken = response.data.jwt;
          this.chatSettingToken = response.data.chatSettingToken;

          if (this.apiToken) {
            if (!isBroadcastWindow) {
              this.messagingService.emit(GET_ACTIVE_GAME_INFO_TRIGGER, { roomName, token: this.apiToken });
            }
          }

          this.setPrivateRoomLoadingStatus(APIStates.LOADED);

          return FirebaseRoomService.roomAccessSuccess(
            roomName,
            sanitizedUsername,
            response.data,
            isGuest,
            this.handleFirebaseAuthError,
            authDone
          );
        }

        return undefined;
      })
      .then(() => {
        if (isPerformerMatchRoomName(roomName)) {
          if (roomStatus === 'roomOffline') {
            this.watchOnlineStatus(roomName);
          } else {
            const isGold = AccessLevelService.isAnyGold(UserStore.user.accessLevel);
            this.watchRoomRoster(roomName, sanitizedUsername);
            this.watchRoomUsers(roomName);
            this.watchRoomState(roomName, sanitizedUsername);
            ChatStore.watchMainChat(roomName, isGold, sanitizedUsername, chatHistory);
            if (getPerformerLocationLowerName() === 'broadcast') {
              BroadcasterPrivateShowStore.watchPrivateShowRequests(roomName);
              this.watchModerationsInfo(roomName);
              this.watchPrivateShowStats(roomName);
            }
            if (!isGuest) {
              ChatStore.watchPrivateChat(roomName, sanitizedUsername);
            }
          }
        }
      })
      .catch((error) => {
        if (isPerformerMatchRoomName(roomName)) {
          this.setPrivateRoomLoadingStatus(APIStates.ERROR);
          this.updateAuthError(FirebaseRoomService.authDataStatusHandler(error));
          ChatStore.setChatAuthentication(false);
          this.messagingService.emit(ROOM_LOADING_STATUS, APIStates.ERROR);
        }
        if (roomStatus === null) {
          roomRequestRetries += 1;
          if (roomRequestRetries < MAX_ROOM_REQUEST_RETRIES) {
            const delay = 2000;
            timeoutId = setTimeout(() => this.initRoom(rawRoomName, username, accessHash, pass, authDone), [delay]);
          } else {
            roomRequestRetries = 0;
            clearTimeout(timeoutId);
            FirebaseRoomService.showErrorModal();
          }
        }
      });
  }


  @action _handleInitRoom = (data) => {
    const { roomName, username, accessHash, password, authDone } = data;
    if (this.roomInitialized === roomName) {
      return;
    }

    this.roomInitialized = roomName;
    this.initRoom(roomName, username, accessHash, password, authDone);
  };

  @action resetPrivateRoom = () => {
    this.authData = {
      ...this.authData,
      status: 'privateRoom',
    };
  };

  @action handleFirebaseAuthError = (
    error,
    roomName,
    username,
    guestNodeName,
    serverTimeOffsetRef,
    cam4FirebaseHost
  ) => {
    if (error) {
      // eslint-disable-next-line no-console
      console.warn('FIREBASE_AUTH_ERROR ', cam4FirebaseHost);
    } else {
      // eslint-disable-next-line no-console
      console.warn('FIREBASE_AUTH_SUCCESS ', cam4FirebaseHost);
      serverTimeOffsetRef.once('value', (snap) => {
        this.serverOffset = snap.val();
      });

      if (!guestNodeName) {
        FirebaseRoomService.initWebPresence(roomName, username);
      } else {
        FirebaseRoomService.guestsPresence(roomName, guestNodeName);
      }
      this.messagingService.emit(UPDATE_WEB_PRESENCE_CHAT_STATUS, true);
      ChatStore.setChatAuthentication(true);
    }

    FirebaseService.FirebaseMain.onAuth(FirebaseService._onAuthLog);
  };

  @action rosterChildChanged = (snapshot, viewerUsername) => {
    const username = snapshot.key();
    const parsedRosterUser = parseLightRosterUser(username, snapshot.val());

    if (viewerUsername.toLowerCase() === parsedRosterUser.originalUsername.toLowerCase()) {
      this.manageModPrivilegeNotif(parsedRosterUser, viewerUsername);
    }
    this.updateUserInRoster(username, parsedRosterUser);
  };

  manageModPrivilegeNotif = (parsedRosterUser, viewerUsername) => {
    const { originalUsername } = parsedRosterUser;
    const prevState = this.webUsers[originalUsername] && this.webUsers[originalUsername].isRoomModerator;
    const currentState = parsedRosterUser.isRoomModerator;
    if (prevState !== currentState && currentState === '1') {
      this.messagingService.emit(MODERATOR_PRIVILEGE_CHANGED, { username: viewerUsername, isRoomModerator: true });
    } else if (prevState !== currentState && currentState === '0') {
      ChatStore.togglePrivateMessages(false);
      ChatStore.resetActiveConversation();
      ChatStore.handleChatInput('');
      this.messagingService.emit(MODERATOR_PRIVILEGE_CHANGED, { username: viewerUsername, isRoomModerator: false });
    }
  };

  @action rosterChildRemoved = (snapshot) => {
    const username = snapshot.key();
    this.removeUserFromRoster(username);
  };

  @action rosterChildAdded = (snapshot, viewerUsername) => {
    const username = snapshot.key();
    const parsedRosterUser = parseLightRosterUser(username, snapshot.val());
    const { accessLevel, originalUsername } = parsedRosterUser;
    const isGold = isAnyTypeOfGold(accessLevel);
    this.updateUserInRoster(username, parsedRosterUser);
    const notificationExists = ChatStore.messages.find((msg) => msg.messageKey === originalUsername);
    const broadcastWindow = location.pathname === '/broadcast';
    const isLoggedInUserEnterRoster = viewerUsername === originalUsername?.toLowerCase();
    const broadcasterSide = broadcastWindow && !isLoggedInUserEnterRoster;
    const viewerSide = !broadcastWindow && isLoggedInUserEnterRoster;
    const useChatBots = UserStore.hasCF(allUserGroups.CHAT_BOTS);
    if (isGold && (broadcasterSide || viewerSide) && parsedRosterUser.notifyBroacaster === '1' && !notificationExists) {
      ChatStore.addMessage({
        type: NOTIFICATION_TYPE.GOLD_ENTERED_ROOM,
        senderOriginalUsername: originalUsername,
        messageKey: originalUsername,
      });
    }
    if (viewerSide && useChatBots) {
      chatBotsStore.checkWelcomeBotStatusAndSendMessage();
    }
  };

  @action watchRoomState = (roomname) => {
    FirebaseService.watchThread(roomname, 'roomStateRef')
      .once('value', (snapshot) => {
        this.populateRoomStateData(snapshot.val());
      })
      .then(() => {
        this.roomDataReady = true;
        FirebaseService.watchThread(roomname, 'roomStateRef').on('value', (snapshot) => {
          const roomState = snapshot.val();
          if (isBroadcastWindow && !roomState) {
            this.removeBroadcasterFirebaseListeners(roomname);
            this.removeFirebaseListeners(roomname);
            this.roomInitialized = undefined;
            this.messagingService.emit(SHOW_ENDED, { roomname });
          }
          if (roomState) {
            this.populateRoomStateData(roomState);
          }
        });
      });
  };

  @action watchRoomUsers = (roomname) => {
    FirebaseService.watchThread(roomname, 'chatlessRoomUserCountRefLight')
      .once('value', (snapshot) => {
        this.userCountString = snapshot.val();
      })
      .then(() => {
        this.roomDataReady = true;
        FirebaseService.watchThread(roomname, 'chatlessRoomUserCountRefLight').on('value', (snapshot) => {
          if (snapshot.val()) {
            this.userCountString = snapshot.val();
          }
        });
      });
  };

  @action watchRoomRoster = (roomName, username) => {
    FirebaseRoomService.rosterChildChanged(roomName, this.rosterChildChanged, username);
    FirebaseRoomService.rosterChildAdded(roomName, this.rosterChildAdded, username);
    FirebaseRoomService.rosterChildRemoved(roomName, this.rosterChildRemoved, username);
  };

  watchModeratorsInfo = (roomName) => {
    FirebaseService.watchThread(roomName, 'moderatorRef').on('value', (snapshot) => {
      this.messagingService.emit(SET_BROADCASTER_MODERATORS_IN_ROOM, snapshot.val());
    });
  };

  watchSilencedUsersInfo = (roomName) => {
    FirebaseService.watchThread(roomName, 'silencedRef').on('value', (snapshot) => {
      this.messagingService.emit(SET_BROADCASTER_SILENCED_USERS_IN_ROOM, snapshot.val());
    });
  };

  watchBannedUsersInfo = (roomName) => {
    FirebaseService.watchThread(roomName, 'bannedRef').on('value', (snapshot) => {
      this.messagingService.emit(SET_BROADCASTER_BANNED_USERS_IN_ROOM, snapshot.val());
    });
  };

  watchUserOfflineNotifications = (username, token, firebaseShard) => {
    this.initializeUserNotificationFirebaseMain(token, firebaseShard).then(() => {
      this.notificationFirebaseService.watchThread(username, 'userOfflineNotificationRef').on('value', (snapshot) => {
        this.messagingService.emit(USER_OFFLINE_NOTIFICATION_RECEIVED, snapshot.val());
      });
    });
  };

  initializeUserNotificationFirebaseMain = (token, firebaseShard) => {
    this.notificationFirebaseService.initFirebase(firebaseShard, 'Notification');

    return this.notificationFirebaseService.authWithCustomToken(token);
  };

  watchModerationsInfo = (roomName) => {
    this.watchSilencedUsersInfo(roomName);
    this.watchModeratorsInfo(roomName);
    this.watchBannedUsersInfo(roomName);
  };

  removeBroadcasterFirebaseListeners = (roomName) => {
    this._setJwt(null);
    FirebaseService.watchThread(roomName, 'privateShowStatsRef')?.off();
    FirebaseService.watchThread(roomName, 'privateShowRequests')?.off();
    FirebaseService.watchThread(roomName, 'silencedRef')?.off();
    FirebaseService.watchThread(roomName, 'bannedRef')?.off();
    FirebaseService.watchThread(roomName, 'bannedRef')?.off();
  };

  watchPrivateShowStats = (roomName) => {
    FirebaseService.watchThread(roomName, 'privateShowStatsRef').on('value', (snapshot) => {
      this.messagingService.emit(SET_BROADCASTER_PRIVATE_SHOW_INFO, snapshot.val());
    });
  };

  isFanClubSubscribed = (username) =>
    computed(() => this.webUsers[username] && this.webUsers[username].isFanclubSubscribed == 1).get();

  @computed get isStreamPortrait() {
    return (
      this.streamResolution &&
      parseInt(this.streamResolution.split(':')[0], 10) < parseInt(this.streamResolution.split(':')[1], 10)
    );
  }

  @computed get isStreamingSaas() {
    return this.authData.useStreamingSaas === true;
  }

  @computed get isAllowedStreamInfoCall() {
    return this.online === null || (this.isStreamingSaas === false && this.online === true);
  }

  @action watchOnlineStatus = (roomName) => {
    const { hasCF } = UserStore;
    if (hasCF(allUserGroups.BROADCASTER_ONLINE_PROFILE_V2)) {
      FirebaseService.watchThread(roomName, 'broadcastingStatus').on('child_added', (snapshot) => {
        if (snapshot.val() === 1) {
          setTimeout(() => {
            this.messagingService.emit(USER_BROADCAST_STARTED, { roomName });
          }, STREAM_DETAILS_DELAY_MS);
          // Due to Backend Propagation issues we need at least 5 secs in order to have the stream details
        }
      });
    }
  };

  @computed get sortedUsers() {
    function sortAlphabetically(a, b) {
      return a.originalUsername.toLowerCase() < b.originalUsername.toLowerCase() ? -1 : 1;
    }
    const usersArray = Object.keys(this.webUsers).map((user) => this.webUsers[user]);
    const coachUsers = usersArray.filter((user) => ACCESS_LEVEL_STR.COACH === user.accessLevel);
    const verifiedRoomModerators = usersArray.filter(
      (user) => ACCESS_LEVEL_STR.VERIFIED_ROOM_MODERATOR === user.accessLevel
    );
    const assignedRoomModerators = usersArray.filter((user) => user.isRoomModerator == 1);
    const goldUsers = usersArray.filter(
      (user) =>
        isAnyTypeOfGold(user.accessLevel) &&
        ACCESS_LEVEL_STR.COACH !== user.accessLevel &&
        ACCESS_LEVEL_STR.VERIFIED_ROOM_MODERATOR !== user.accessLevel &&
        user.isRoomModerator != 1
    );
    const regularUsers = usersArray.filter(
      (user) =>
        !isAnyTypeOfGold(user.accessLevel) &&
        ACCESS_LEVEL_STR.COACH !== user.accessLevel &&
        ACCESS_LEVEL_STR.VERIFIED_ROOM_MODERATOR !== user.accessLevel &&
        user.isRoomModerator != 1
    );

    const sortedCoachUsers = coachUsers.sort(sortAlphabetically);
    const sortedVerifiedRoomModerators = verifiedRoomModerators.sort(sortAlphabetically);
    const sortedAssignedRoomModerators = assignedRoomModerators.sort(sortAlphabetically);
    const sortedGoldUsers = goldUsers.sort(sortAlphabetically);
    const sortedRegularUsers = regularUsers.sort(sortAlphabetically);

    const uniqueUsers = [
      ...new Set(
        sortedCoachUsers.concat(
          sortedVerifiedRoomModerators,
          sortedAssignedRoomModerators,
          sortedGoldUsers,
          sortedRegularUsers
        )
      ),
    ];

    return uniqueUsers;
  }

  @computed get userCountLight() {
    return this.sortedUsers.length;
  }

  @computed get totalUsers() {
    if (!this.userCountString) {
      return 0;
    }
    const [totalLoggedInUsers, globalTotalGuests] = this.userCountString.split('$');

    return parseInt(totalLoggedInUsers, 10) + parseInt(globalTotalGuests, 10);
  }

  @computed get totalLoggedInUsers() {
    if (!this.userCountString) {
      return 0;
    }
    const loggedInUsers = this.userCountString.split('$')[0];

    return parseInt(loggedInUsers, 10);
  }

  @computed get totalGuestUsers() {
    if (!this.userCountString) {
      return 0;
    }
    const guestUsers = this.userCountString.split('$')[1];

    return parseInt(guestUsers, 10);
  }

  isLoggedInUserRoomModerator = (username) =>
    computed(() => this.webUsers[username] && this.webUsers[username].isRoomModerator == 1).get();

  isUserSilencedByRoomModerator = (username) =>
    computed(() => this.webUsers[username] && this.webUsers[username].isSilenced === true).get();

  isLoggedInUserBroadcasting = (username) =>
    computed(() => this.webUsers[username] && this.webUsers[username].isBroadcasting === 'm').get();

  @action togglePrivateShowRequest = (val) => {
    this.isPrivateShowRequestActive = val !== undefined ? val : !this.isPrivateShowRequestActive;
  };

  @action _handleKickOrBan = (kbData) => {
    const { kickOrBan, viewerUsername } = kbData;
    this.removeUserFromRoster(viewerUsername);

    if (kickOrBan === 'k') {
      this.userKickedStatus = true;
    } else if (kickOrBan === 'b') {
      this.userBannedStatus = true;
    } else if (kickOrBan === 'pb') {
      this.userPermaBannedStatus = true;
    } else if (kickOrBan === 'cr') {
      this.closedRoomStatus = true;
    } else if (kickOrBan === 'p') {
      this.userNeedsId = true;
    }
  };

  @computed get needsId() {
    return this.userNeedsId;
  }

  @computed get isClosedRoom() {
    return this.closedRoomStatus;
  }

  @computed get isKicked() {
    return this.userKickedStatus;
  }

  @computed get isBanned() {
    const bannedForRoom = this.authData.status === FIREBASE_AUTH_STATUS.bannedForRoom;

    return this.userBannedStatus || this.userPermaBannedStatus || bannedForRoom;
  }

  @computed get shouldSendC2CMetric() {
    return this.allowCamToCam === 1 || this.allowCamToCam === '1';
  }

  @computed get isPrivateRoom() {
    const { status, privateRoom } = this.authData;

    // set private room only if status is diff. than success
    // if status is success that means this user has access to room
    // even the room is still private
    if (status !== FIREBASE_AUTH_STATUS.success && privateRoom) {
      return true;
    }

    // by default treat room as public
    return false;
  }

  @computed get wrongRoomPassword() {
    const { status } = this.authData;

    return status === FIREBASE_AUTH_STATUS.wrongPassword;
  }

  @computed get privateShowTimeRemain() {
    if (this.newShowsDetails && this.newShowsDetails.set) {
      return parseInt((this.newShowsDetails.set - this.newShowsDetails.startTime) / SECOND_AS_MILLISECONDS, 10);
    }

    return null;
  }

  @computed get isEndingPrivateShowLoading() {
    return this.cancelLoadingStatePrivateShow === APIStates.PENDING;
  }

  @computed get isEnteringPrivateShowLoading() {
    return this.privateShowLoadingButtonState === APIStates.PENDING;
  }

  @computed get isPrivateRoomLoading() {
    return this.privateRoomLoading === APIStates.PENDING;
  }

  isUserInRoster = (username) =>
    computed(() => this.webUsers && Object.keys(this.webUsers).some((name) => name === username)).get();

  userHavePSTicket = (username) =>
    computed(
      () =>
        this.newShowsDetails !== undefined &&
        this.newShowsDetails !== null &&
        this.newShowsState === NEW_SHOWS_STATE.INSIDE_PS &&
        Object.prototype.hasOwnProperty.call(this.newShowsDetails, 'ticketHolders') &&
        this.newShowsDetails.ticketHolders.includes(username && username.toLowerCase())
    ).get();

  @action resetFirebaseStoreState = () => {
    Object.keys(firebaseStoreInitialState).forEach((item) => (this[item] = firebaseStoreInitialState[item]));
  };

  @action resetNewShowState = () => {
    if (isBroadcastWindow) {
      this.newShowsState = null;
    }
  };

  @action setBlocksOfTimeIndex = (index) => {
    this.blocksOfTimeIndex = index;
  };

  // does not iclude solo private
  userInPrivateOrGroupShow = (username) =>
    computed(
      () =>
        (this.broadcasterInPrivateShow &&
          this.newShowsDetails?.ticketHolders[0] !== username?.toLowerCase()) ||
        this.newShowsState === NEW_SHOWS_STATE.GS_SELLING_TICKETS ||
        this.newShowsState === NEW_SHOWS_STATE.INSIDE_GS ||
        this.newShowsState === NEW_SHOWS_STATE.GS_GOAL_REACHED ||
        this.newShowsState === NEW_SHOWS_STATE.BONUS_GS
    ).get();

  isAnotherUserInC2C = (username) =>
    computed(
      () =>
        (this.broadcasterInPrivateShow &&
        this.newShowsDetails?.ticketHolders[0] !== username?.toLowerCase())
    ).get();

  @computed get broadcasterInPrivateShow() {
    return this.newShowsState === NEW_SHOWS_STATE.INSIDE_PS;
  }

  @computed get broadcastPaused() {
    return this.newShowsState === NEW_SHOWS_STATE.PAUSED;
  }

  @computed get broadcastPausedTimerExpired() {
    return this.newShowsState === NEW_SHOWS_STATE.ACCEPTING && !!this.pauseTimestamp && this.online === false;
  }

  @computed get billedUsername() {
    return this.newShowsDetails?.ticketHolders[0];
  }

  @computed get showType() {
    let showType = SHOW_TYPE.PUBLIC;
    const { playToken, profileName } = ProfileStore;
    const inSoloPrivateShow = this.newShowsDetails?.ticketHolders?.[0] === profileName;

    if (!playToken) {
      return showType;
    }

    if (this.broadcasterInPrivateShow) {
      if (this.isCam2CamShow) {
        if (Number(this.privateShowBlocksOfTimeActive)) {
          showType = SHOW_TYPE.C2C_BOT;
        } else {
          showType = SHOW_TYPE.C2C_PPM;
        }
      } else if (inSoloPrivateShow) {
        showType = SHOW_TYPE.SOLO_PRIVATE;
      } else if (Number(this.privateShowBlocksOfTimeActive)) {
        showType = SHOW_TYPE.PRIVATE_BOT;
      } else {
        showType = SHOW_TYPE.PRIVATE_PPM;
      }
    }
    if (EXTENDED_GS_STATES.includes(this.newShowsState)) {
      showType = SHOW_TYPE.GROUP_SHOW;
    }

    return showType;
  }

  @computed get isCam2CamShow() {
    return this.newShowsDetails?.cam2cam && this.newShowsState === NEW_SHOWS_STATE.INSIDE_PS;
  }

  @computed get privateShowPricePerMinute() {
    const { user: { tokenUpchargePercentage } } = UserStore;
    if (UserStore.hasCF(allUserGroups.USE_UPCHARGE) && tokenUpchargePercentage) {
      const newPrice = addPercentage(this.viewerPricePerMinute, tokenUpchargePercentage);

      return newPrice;
    }

    return this.viewerPricePerMinute;
  }

  @computed get c2cPrivateShowPricePerMinute() {
    const { user: { tokenUpchargePercentage } } = UserStore;
    if (UserStore.hasCF(allUserGroups.USE_UPCHARGE) && tokenUpchargePercentage) {
      const newPrice = addPercentage(this.c2cPricePerMinute, tokenUpchargePercentage);

      return newPrice;
    }

    return this.c2cPricePerMinute;
  }

  @computed get spyShowPricePerMinute() {
    const { user: { tokenUpchargePercentage } } = UserStore;
    if (UserStore.hasCF(allUserGroups.USE_UPCHARGE) && tokenUpchargePercentage) {
      const newPrice = addPercentage(this.spyPricePerMinute, tokenUpchargePercentage);

      return newPrice;
    }

    return this.spyPricePerMinute;
  }

  @computed get groupShowTicketPrice() {
    const { user: { tokenUpchargePercentage } } = UserStore;

    const ticketPrice = this.newShowsDetails?.ticketPrice;
    if (!ticketPrice) {
      return 0;
    }

    if (UserStore.hasCF(allUserGroups.USE_UPCHARGE) && tokenUpchargePercentage) {
      const newPrice = addPercentage(ticketPrice, tokenUpchargePercentage);

      return newPrice;
    }

    return ticketPrice;
  }
}

export default new FirebaseStore();
